/**
 * Flag — small SVG flag icon resolved from a language code.
 *
 * Uses `country-flag-icons` 3x2 React SVGs so the flag renders
 * identically across Windows, macOS, Linux, iOS and Android (the
 * old emoji-flag approach showed plain `🇬🇧` as raw "GB" text on
 * Windows Chrome and several other browsers).
 *
 * Mapping table is intentionally explicit: a language is NOT a
 * country (Chinese → CN, English → GB by design choice, Portuguese →
 * PT for European Portuguese), so we hand-pick the country code for
 * each supported language rather than dropping the locale part.
 */
import React from 'react';

// All flags are 3x2 SVGs; we import them statically so the bundler
// includes them at build time (no runtime fetch).
import GB from 'country-flag-icons/react/3x2/GB';
import IT from 'country-flag-icons/react/3x2/IT';
import CN from 'country-flag-icons/react/3x2/CN';
import ES from 'country-flag-icons/react/3x2/ES';
import DE from 'country-flag-icons/react/3x2/DE';
import FR from 'country-flag-icons/react/3x2/FR';
import PT from 'country-flag-icons/react/3x2/PT';
import CZ from 'country-flag-icons/react/3x2/CZ';
import RU from 'country-flag-icons/react/3x2/RU';
import PL from 'country-flag-icons/react/3x2/PL';
import HU from 'country-flag-icons/react/3x2/HU';

const FLAGS = {
  en: GB,
  it: IT,
  'zh-CN': CN,
  zh: CN,
  es: ES,
  de: DE,
  fr: FR,
  pt: PT,
  cs: CZ,
  ru: RU,
  pl: PL,
  hu: HU,
};

/**
 * Render the flag for a given language code. Falls back silently to
 * `null` for unknown codes so callers can safely pass any string.
 *
 * Props:
 *   - code:      i18n language code (e.g. `en`, `zh-CN`)
 *   - className: applied to the SVG (default `w-4 h-3`)
 *   - title:     optional aria-label / tooltip
 */
export default function Flag({ code, className = 'w-4 h-3', title }) {
  const Cmp = FLAGS[code] || FLAGS[code?.split('-')[0]];
  if (!Cmp) return null;
  return (
    <Cmp
      className={`${className} rounded-[2px] shadow-[0_0_0_0.5px_rgba(0,0,0,0.08)] inline-block`}
      title={title}
      aria-label={title || code}
    />
  );
}
